import { Accessor, createEffect, createMemo, createResource, createSignal, For, JSXElement, Match, onMount, Resource, ResourceActions, ResourceFetcherInfo, Setter, Show, Switch } from "solid-js";
import { Currency, DataContext, get_counts, get_items, image_url, ImageId, ImageInfo, Item, ItemId, log_error, PublicItem } from "./api";
import { area, compareAttrVal, compareMap, convert_price, filterMap, format_price, format_size, map_to_object, parse_attr_string, parse_id_map, select_size_from_width, stringify_attr_val } from "./util";
import { A, Route, Router, useLocation, useNavigate, useParams } from "@solidjs/router";
import { render } from "solid-js/web";
import { AppProvider, AttrMap, NavigationState, useApp } from "./context";
import { Cart, CartAction, Icon } from "./cart2";
import { CurrencySelect, Header, ItemHeader, ShopHeader } from "./header";
import { User, UserLogin } from "./login";
import { OrderForm } from "./checkout";
import { AttrId } from "./bindings/AttrId";
import { AttrVal } from "./bindings/AttrVal";
import { CategoryId } from "./bindings/CategoryId";
import { AttrList, RequireUser, tr_str, tr_text } from "./common";
import { Account } from "./account";

const [scroll, setScroll] = createSignal<number>();
const [windowWidth, setWindowWidth] = createSignal(window.innerWidth);
const margin = 2.;
const numCols = createMemo(() => Math.max(Math.min(Math.floor(windowWidth() / 400), 4), 1));
const colWidth = () => {
    const n = numCols();
    return (100 - margin * (n + 1)) / n;
};
createEffect(() => console.log("numCols:", numCols()));
const imageSize = () => select_size_from_width(windowWidth() * colWidth() * window.devicePixelRatio / 100);
const fullscreenSize = () => select_size_from_width(windowWidth() * window.devicePixelRatio);

onMount(() => {
    window.addEventListener("resize", () => {
        setWindowWidth(window.innerWidth);
    });
    window.addEventListener("scroll", (e) => setScroll(window.scrollY));
});

type ItemSetView = {
    ids: ItemId[];
    complete: boolean;
    attrs: AttrMap;
    category: CategoryId | null;
    clear: boolean;
};
type AttrPair = [AttrId, string];
type Hash = {
    attrs?: AttrPair[],
    category?: CategoryId,
    id?: ItemId[],
    cart?: boolean,
    order?: boolean,
};
function build_link(hash: Hash) {
    let parts: string[] = [];
    if (hash.cart) {
        parts.push("cart")
    }
    if (hash.order) {
        parts.push("order")
    }
    if (hash.id !== undefined && (hash.id?.length ?? 0 > 0)) {
        parts.push(`id=${hash.id.join("+")}`);
    }
    if (hash.category !== undefined) {
        parts.push(`cat=${hash.category}`);
    }
    if (hash.attrs !== undefined && (hash.attrs.length ?? 0 > 0)) {
        hash.attrs.sort((a, b) => a[0] - b[0]);
        const s = `a=${hash.attrs.map(([id, val]) => `${id}:${val}`).join("+")}`;
        parts.push(s);
    }
    if (parts.length > 0) {
        return `/shop#${parts.join(",")}`;
    } else {
        return "/shop";
    }
}
function parse_attr_pair(s: string): [AttrId, string] {
    const [a, val] = s.split(":", 2);
    return [parseInt(a), val];
}
function parse_hash(s: string | undefined): Hash {
    const hash: Hash = { id: [], attrs: [], cart: false, order: false };
    if (s === undefined) {
        return hash;
    }
    if (s.startsWith("#")) {
        s = s.substring(1);
    }
    for (const p of s.split(",")) {
        const [prefix, data] = p.split("=");
        switch (prefix) {
            case "cat":
                hash.category = parseInt(data);
                break;
            case "a":
                hash.attrs = data.split("+").map(parse_attr_pair);
                break;
            case "id":
                hash.id = data.split("+").map(s => parseInt(s));
                break;
            default:
                hash[prefix] = true;
        }
    }
    return hash;
}


function ListItem({id, state}: {id: ItemId, state: NavigationState}): [HTMLElement, number] {
    const { cart, cache, user } = useApp();
    const item: PublicItem = cache.get_item(id);

    if (item.images.length === 0) {
        return null;
    }
    const image_id = item.images[0];
    const image = cache.get_img(image_id);
    const img_width = colWidth();
    const img_height = img_width * image.aspect_ratio;
    const attr_map = parse_id_map(item.attrs);
    const price = () => {
        const p = item.price;
        if (p === null || !item.in_stock) return null;
        return convert_price(cache.fx(), p, user.currency());
    };

    const m2_price = () => {
        const m2 = area(cache.attr_by_name(), attr_map);
        const p = price();
        if (m2 === undefined || p === null) return null;
        return { currency: p.currency, val: p.val / m2 };
    }
    const filter = () => {
        const attrs = state.attrs();
        if (attrs.size === 0) return (id, val) => true;
        return (id, val) => val.type !== "Enum" && (attrs.has(id) || val.type === "Length");
    }
    
    let show_info = () => (price() !== null || attr_map.entries().filter(filter()).next().done === false);
    createEffect(() => console.log("info=", show_info(), new Array(attr_map.entries), attr_map.entries().filter(filter()).next() ));
    return [(
        <div class="item">
            <A href={"/item/"+id}><img style={{
                width: `${img_width}vw`,
                height: `${img_height}vw`,
            }} src={image_url(image_id, imageSize())} /></A>
            <Show when={show_info()}>
            <div class="info grid">
                <dl>
                <Show when={price()} >
                    <dt>Price</dt><dd>{format_price(price())}</dd>
                </Show>
                <Show when={m2_price()}>
                    <dt>Price/m²</dt><dd>{format_price(m2_price())}</dd>
                </Show>
                <AttrList attr_map={attr_map} filter={filter()} />
                </dl>

                <Show when={!item.in_stock}>
                    <h3>{tr_str("out of stock", user.lang())}</h3>
                </Show>
            </div></Show>
            <CartAction id={id} />
        </div>
    ) as HTMLElement, img_height];
};


type RefetchInfo = {
    n: number
};
async function loadItemSet(
    {category, attrs, currency}: {category: CategoryId, attrs: AttrMap, currency: Currency | null},
    { value, refetching }: ResourceFetcherInfo<ItemSetView, RefetchInfo>
): Promise<ItemSetView> {
    console.log("loadItemSet", attrs);
    const { cache } = useApp();
    
    const same_key = (value !== undefined) && value.category === category && compareMap(value.attrs, attrs, compareAttrVal);
    console.log("same_key", same_key);
    if (!same_key)
        value = undefined;

    const prev = value?.ids ?? [];
    const complete = value?.complete ?? false;
    const start = prev.length;
    const end = (refetching !== false && refetching !== true) ? refetching.n : 100;
    if (same_key && complete) {
        return { ids: prev, complete: true, attrs, category, clear: false };
    }
    if (end > start) {
        const set = await get_items({
            attrs: map_to_object(attrs),
            limit: end - start,
            id: [],
            category
        });
        cache.add_set(set);
        
        return {
            ids: [...prev, ...Array.from(set.items, ([id, _]) => id)],
            complete: set.complete,
            attrs, category, clear: !same_key
        };
    }
    return { ids: [], complete: false, attrs, category, clear: false };
}


function FullscreenComponent({id, item}: { id: number, item: PublicItem }) {
    const img_id = item.images[0];
    const attr_map = parse_id_map(item.attrs);
    const { user, cache, cart } = useApp();
    const lang = user.lang;

    return (<div>
        <ItemHeader />
        <div class="fullscreen">
            <div class="frame grid">
                <dl>
                    <dt>{tr_str("Item Nr", lang())}</dt>
                    <dd>{id}</dd>
                    <dt>{tr_str("Price", lang())}</dt>
                    <dd>{format_price(item.price)}</dd>
                    <AttrList attr_map={attr_map} />
                </dl>
            </div>
            <CartAction id={id} />
            <div>
                <div class="fullwidth"><img src={image_url(img_id, fullscreenSize())} /></div>
            </div>
        </div>
    </div>
    );
}

function FullscreenItem({id}: {id: number}) {
    const { cache } = useApp();
    const [item] = createResource(() => cache.get_item_async(id));

    return (<div><Header />
    <Show when={item()} keyed fallback={"loading..."}>
        { (item) => <FullscreenComponent id={id} item={item} /> }
    </Show></div>);
}

function FullScreenWrap(): JSXElement {
    const params = useParams();
    const id = parseInt(params.id);
    return FullscreenItem({id});
}

class Columns {
    element: JSXElement;

    cols: HTMLElement[];
    heights: number[];

    count: number;

    constructor(n: number, load_more: (n: number) => void, show_more: Accessor<boolean>) {
        this.cols = [];
        this.heights = [];
        this.count = 0;

        for (let i = 0; i < n; i++) {
            const col = (<div class="column"></div>) as HTMLElement;
            this.cols.push(col);
            this.heights.push(0);
        }
        this.element = (
            <div class="flow">
                <For each={this.cols}>
                    {col => {                
                        const [element, setElement] = createSignal<HTMLDivElement>();
                        const delta = () => {
                            let _ = scroll();
                            let e = element();
                            if (e !== undefined) {
                                const r = e.getBoundingClientRect();
                                return r.y - window.innerHeight;
                            }
                        };
                        createEffect(() => {
                            let d = delta();
                            if (d !== undefined) {
                                if (d < 100) {
                                    load_more(this.count + 10);
                                }
                            }
                        });
                        return (<div class="column" style={{width: `${colWidth()}vw`}}>{col}
                            <Show when={show_more()}>
                                <div ref={setElement}>
                                    <button on:click={() => load_more(this.count + 10)}>more</button>
                                </div>
                            </Show>
                            </div>
                        );
                    }}
                </For>
            </div>
        );
    }

    add(element: HTMLElement, height: number) {
        let min_idx = 0;
        let min_height = Infinity;
        for (const [i, h] of this.heights.entries()) {
            if (h < min_height) {
                min_idx = i;
                min_height = h;
            }
        }
        this.heights[min_idx] += height;
        this.cols[min_idx].appendChild(element);
        this.count += 1;
    }

    clear() {
        this.cols.forEach(e => e.replaceChildren());
        this.heights.fill(0);
        this.count = 0;
    }
}

function ShopColumns({state, view, setView}: {state: NavigationState, view: Resource<ItemSetView>, setView: ResourceActions<ItemSetView>}) {
    console.log("reloading columns");
    const [numVisible, setNumVisible] = createSignal(12);
    const show_more = () => {
        if (view.loading) return false;
        const v = view();
        return !(v.complete && numVisible() >= view.latest.ids.length);
    };
    createEffect(() => console.log("show_more =", show_more()));

    function add_slice(items: ItemSetView, start: number, end?: number) {
        const col = cols();
        items.ids.slice(start, end).forEach(id => {
            const item = ListItem({id, state});
            if (item !== null) {
                const [elem, height] = item;
                col.add(elem, height);
            }
        });
        return items.ids.length;
    }

    function more(n: number) {
        setNumVisible((last) => last + 10);
    }
    const cols = createMemo(() => new Columns(numCols(), more, show_more), undefined, {equals: (a, b) => a.cols.length === b.cols.length});
    
    return <Show keyed when={cols()}>
        {(cols) => {
            createEffect((last: number) => {
                const items = view();
                const limit = numVisible();
                if (items !== undefined) {
                    if (items.clear) {
                        cols.clear();
                        last = 0;
                        console.log("clearing");
                    }
                    const n = items.ids.length;
                    if (limit > n) {
                        add_slice(items, last, n);
                        if (!items.complete) {
                            setView.refetch({ n: n + 100 });
                        }
                        return n;
                    } else {
                        add_slice(items, last, limit);
                        return limit;
                    }
                }
                return 0;
            }, 0);
            return cols.element;
        }}
    </Show>;
}

function Shop() {
    const { cache } = useApp();
    const hash = () => parse_hash(useLocation().hash);
    createEffect(() => console.log("hash", useLocation().hash));

    const navigate = useNavigate();
    createEffect(() => {
        const h = hash();
        if (h.id !== undefined && h.id.length == 1) {
            const url = `/item/${h.id[0]}`;
            console.log("redirecting to item", url);
            navigate(url);
        }
    });

    const attrs_list = () => hash().attrs;
    createEffect(() => console.log("attrs_list", attrs_list()));
    const attrs_map = createMemo<AttrMap>(() => {
        const attrs = cache.attrs();
        if (attrs === undefined) return;

        const map = ([id, val]) => {
            const def = attrs.get(id);
            if (def === undefined) return;
            const attr: [AttrId, AttrVal] = [id, parse_attr_string(def, val)];
            return attr;
        }
        return new Map(attrs_list().map(map).filter(v => v !== undefined));
    });
    const cart = createMemo(() => hash().cart);
    const order = createMemo(() => hash().order);
    const category = createMemo(() => hash().category);

    createEffect(() => {
        console.log("attrs_map", attrs_map());
    });

    function apply(hash: Hash) {
        function merge<T>(a: T | undefined, b: () => T): T {
            let val = a;
            if (a === undefined) {
                val = b();
            }
            return val !== null ? val : undefined;
        }
        const merged = {
            attrs: merge(hash.attrs, attrs_list),
            cart: merge(hash.cart, cart),
            order: merge(hash.order, order),
            category: merge(hash.category, category),
        };
        console.log("apply", hash, "->", merged);
        const link = build_link(merged);
        console.log("GOTO", link);
        navigate(link, { replace: false })
    }
    function setAttrs(attr_map: AttrMap) {
        const attrs: [AttrId, string][] = Array.from(attr_map.entries(), ([id, val]) => [id, stringify_attr_val(val)]);
        attrs.sort(([a, _a], [b, _b]) => a - b);
        apply({attrs});
    }
    function setAttr(id: AttrId, val: AttrVal | null) {
        console.log("setAttr", id, val);
        const map = attrs_map();
        if (val === null) {
            if (!map.has(id)) return;
            
            const map2 = filterMap(map, ([k, v]) => k !== id);
            setAttrs(map2);
        } else {
            const old_val = map.get(id);
            if (old_val !== undefined && compareAttrVal(val, old_val)) return;

            const map2 = new Map(map.entries());
            map2.set(id, val);
            setAttrs(map2);
        }
    }
    function setCategory(cat: CategoryId | null) {
        if (cat !== category()) {
            apply({category: cat, attrs: []});
        }
    }
    const [counts] = createResource(category, get_counts);
    
    const state: NavigationState = { attrs: attrs_map, setAttrs, setAttr, category, setCategory, counts };
    const key = createMemo(() => {
        const attrs = attrs_map();
        if (attrs === undefined) return;
        return { attrs, category: category()};
    }, undefined, {equals: false});
    createEffect(() => console.log("key", key()));

    const [view, setView] = createResource(key, loadItemSet, { name: "items" });
    createEffect(() => console.log("view", view()));
    const attr_set = () => {
        const set: Set<AttrId> = new Set();
        const latest = view();
        if (latest === undefined) return;
        
        for (const id of latest.ids) {
            const item = cache.get_item(id);
            const attrs = parse_id_map(item.attrs);
            for (const id of attrs.keys()) {
                set.add(id);
            }
        }
        return set;
    };

    return (<div>
        <Header />
        <Switch fallback={<>
                <ShopHeader state={state} attr_set={attr_set} />
                <Show when={key()} keyed>{
                    ({attrs, category}) => <ShopColumns state={state} view={view} setView={setView} />
                }</Show>
                </>
        }>
            <Match when={order()}>
                <div id="fixed">
                </div>
                <div class="content">
                    <div class="frame">
                        <RequireUser>
                            <OrderForm />
                        </RequireUser>
                    </div>
                </div>
            </Match>
            <Match when={cart()}>
                <Cart />
            </Match>
        </Switch>
    </div>);
}

addEventListener("error", log_error);

render(() => (
    <AppProvider>
    <Router url="" explicitLinks={true}>
        <Route path="/item/:id" component={FullScreenWrap} />
        <Route path="/shop" component={Shop} />
        <Route path="/account" component={Account} />
    </Router>
    </AppProvider>
), document.body);
